(() => {
  'use strict';
  if (window.parkCartFlowInstalled) return;
  window.parkCartFlowInstalled = true;
  function initialize() {
  window.ParkCartNotificationFlow?.destroy?.();
  const events = new AbortController();
  const on = (target, type, handler) => target.addEventListener(type, handler, {signal: events.signal});
  const dialog = document.querySelector('[data-park-cart-notification]');
  if (!dialog || dialog.dataset.flowReady) return;
  dialog.dataset.flowReady = 'true';
  const loading = dialog.querySelector('[data-notification-loading]');
  const error = dialog.querySelector('[data-notification-error]');
  const errorText = dialog.querySelector('[data-drawer-error-text]');
  const defaultError = errorText.textContent;
  let revision = 0, timer, activeRead, changing = false, keepRecommendations = false, freshUntil = 0;

  function lock(locked) {
    dialog.setAttribute('aria-busy', String(locked));
    const checkout = dialog.querySelector('[data-drawer-checkout]');
    if (locked) checkout?.setAttribute('aria-disabled', 'true');
    else checkout?.removeAttribute('aria-disabled');
    dialog.querySelectorAll('[data-drawer-quantity], [data-upsell-add], [data-upsell-choose]').forEach(button => {
      if (locked && !button.disabled) {
        button.dataset.drawerLocked = 'true';
        button.disabled = true;
      } else if (!locked && button.dataset.drawerLocked) {
        button.disabled = false;
        delete button.dataset.drawerLocked;
      }
    });
  }
  const renderedResults = new WeakSet();
  const selectors = ['[data-drawer-body]', '[data-drawer-footer]', '[data-drawer-count]'];
  const sectionRequest = () => {
    const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
    if (dialog.dataset.previewThemeId) url.searchParams.set('preview_theme_id', dialog.dataset.previewThemeId);
    return {sections: [dialog.dataset.contextSection], sections_url: url.pathname + url.search};
  };
  function parse(html) {
    if (typeof html !== 'string') throw new Error('Section missing');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = selectors.map(selector => doc.querySelector(selector));
    if (fresh.some(node => !node)) throw new Error('Section missing');
    return fresh;
  }
  function apply(fresh, focus) {
    const oldList = dialog.querySelector('.cc-park-cart-upsell__list');
    const newList = fresh[0].querySelector('.cc-park-cart-upsell__list');
    if (keepRecommendations && oldList && newList) newList.replaceChildren(...Array.from(oldList.children));
    const count = Number(fresh[2].dataset.cartCount);
    selectors.forEach((selector, index) => dialog.querySelector(selector).replaceChildren(...Array.from(fresh[index].childNodes)));
    if (Number.isInteger(count) && count >= 0) {
      dialog.querySelector('[data-drawer-count]').dataset.cartCount = String(count);
      window.theme?.updateCartCount?.(count);
    }
    keepRecommendations = true;
    freshUntil = Date.now() + 15000;
    loading.hidden = true; error.hidden = true; lock(false);
    // Preserved recommendations must reflect the complete server-rendered cart,
    // not the partial items returned by cart/add.js or their previous button state.
    const products = new Set(Array.from(dialog.querySelectorAll('[data-drawer-product]'), row => row.dataset.drawerProduct));
    dialog.querySelectorAll('[data-upsell-product]').forEach(card => {
      const button = card.querySelector('[data-upsell-label]');
      if (!button) return;
      const added = products.has(card.dataset.upsellProduct);
      const addedLabel = card.closest('park-cart-upsell').dataset.addedLabel;
      card.classList.toggle('is-added', added);
      button.disabled = added;
      button.textContent = added ? addedLabel : button.dataset.upsellLabel;
      button.setAttribute('aria-label', added ? addedLabel : button.dataset.upsellAccessibleLabel);
      delete button.dataset.drawerLocked;
    });
    if (focus && dialog.open) {
      const row = Array.from(dialog.querySelectorAll('[data-drawer-line]')).find(node => node.dataset.drawerLine === focus.key);
      const target = row?.querySelector(`[data-drawer-action="${focus.action}"]`);
      if (target && !target.disabled) target.focus();
      else dialog.querySelector('[data-cart-notification-close]').focus();
    }
  }
  function consume(result, focus) {
    if (events.signal.aborted) return false;
    if (result && renderedResults.has(result)) return true;
    const html = result?.sections?.[dialog.dataset.contextSection];
    if (typeof html !== 'string') return false;
    try {
      const fresh = parse(html);
      clearTimeout(timer); ++revision; activeRead?.abort();
      apply(fresh, focus);
      renderedResults.add(result);
      return true;
    } catch (_) { return false; }
  }
  // Only reads may be retried. A timed-out cart mutation may already have succeeded.
  async function readSection(url, current) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const controller = new AbortController();
      activeRead = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url, {cache: 'no-store', signal: controller.signal});
        if (!response.ok) throw new Error('Section unavailable');
        return parse(await response.text());
      } catch (failure) {
        if (current !== revision || attempt === 1) throw failure;
      } finally {
        clearTimeout(timeout);
        if (activeRead === controller) activeRead = null;
      }
    }
  }
  async function update(focus) {
    if (events.signal.aborted) return false;
    freshUntil = 0;
    const current = ++revision;
    activeRead?.abort();
    lock(true); loading.hidden = false; error.hidden = true;
    try {
      const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
      url.searchParams.set('section_id', dialog.dataset.contextSection);
      // Preview cookies can expire independently of an already-open draft page.
      // Use the rendered draft ID, never a production-theme fallback.
      const previewId = dialog.dataset.previewThemeId;
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      const fresh = await readSection(url, current);
      if (current !== revision) return;
      apply(fresh, focus);
      return true;
    } catch (failure) {
      if (current === revision) {
        // Technical reason only: no cart contents or customer data in the log.
        console.warn('[PARK cart] Drawer refresh failed', {
          reason: failure.name === 'AbortError' ? 'timeout' :
            ['Section unavailable', 'Section missing'].includes(failure.message) ? failure.message : 'network-or-render',
          section: dialog.dataset.contextSection
        });
        errorText.textContent = defaultError;
        error.hidden = false;
      }
    } finally {
      if (current === revision) loading.hidden = true;
    }
  }
  function schedule() {
    clearTimeout(timer);
    if (!changing && !events.signal.aborted) timer = setTimeout(() => update(), 50);
  }
  on(dialog, 'close', () => { keepRecommendations = false; });
  const invalidate = () => { freshUntil = 0; };
  on(window, 'focus', invalidate);
  on(window, 'pageshow', event => {
    invalidate();
    // History can restore an already-open drawer after /cart changed its contents.
    // Refresh the server-owned sections and badge without waiting for another open.
    if (event.persisted && !changing) {
      keepRecommendations = false;
      void update();
    }
  });
  on(document, 'visibilitychange', invalidate);
  window.ParkCartNotificationFlow = {
    sectionRequest,
    beginAdd() {
      if (changing || dialog.getAttribute('aria-busy') === 'true' || events.signal.aborted) return false;
      changing = true;
      clearTimeout(timer); ++revision; activeRead?.abort(); freshUntil = 0;
      error.hidden = true; lock(true);
      return true;
    },
    async endAdd(result) {
      if (events.signal.aborted) return false;
      try {
        if (result === false) { lock(false); return true; }
        if (consume(result)) return true;
        keepRecommendations = false;
        const rendered = await update();
        if (!events.signal.aborted && !result) {
          errorText.textContent = defaultError;
          error.hidden = false;
        }
        return rendered;
      } finally { changing = false; }
    },
    isRendered: result => !!result && renderedResults.has(result),
    opened(result) { if (!consume(result) && Date.now() >= freshUntil) schedule(); },
    destroy() { events.abort(); clearTimeout(timer); ++revision; activeRead?.abort(); delete dialog.dataset.flowReady; }
  };
  on(document, 'shopify:section:unload', event => { if (event.target.contains(dialog)) window.ParkCartNotificationFlow?.destroy(); });
  on(dialog.querySelector('[data-notification-retry]'), 'click', () => {
    if (!changing) update();
  });
  on(document, 'theme:cartchanged', event => {
    if (event.detail?.cartDrawerUpdated || (event.detail?.cart && renderedResults.has(event.detail.cart))) return;
    invalidate();
    // An uncertain upsell may have been added. Let Liquid exclude current cart
    // products instead of preserving an old, possibly misleading recommendation.
    if (event.detail?.refreshRecommendations) keepRecommendations = false;
    if (dialog.open) schedule();
  });
  on(dialog, 'click', async event => {
    if (event.target.closest('[data-drawer-checkout][aria-disabled="true"]')) {
      event.preventDefault();
      return;
    }
    const button = event.target.closest('[data-drawer-quantity]');
    if (!button || button.disabled || changing) return;
    const row = button.closest('[data-drawer-line]');
    const quantity = Number(button.dataset.drawerQuantity);
    if (!row || !Number.isInteger(quantity) || quantity < 0) return;
    const focus = {key: row.dataset.drawerLine, action: button.dataset.drawerAction};
    changing = true;
    clearTimeout(timer); ++revision; activeRead?.abort();
    lock(true); loading.hidden = false; error.hidden = true;
    freshUntil = 0;
    let mutationError, cart;
    const mutationController = new AbortController();
    const mutationTimeout = setTimeout(() => mutationController.abort(), 10000);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST', signal: mutationController.signal, headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: row.dataset.drawerLine, quantity, ...sectionRequest()})
      });
      cart = await response.json();
      if (!response.ok || cart.status >= 400) {
        mutationError = typeof cart.description === 'string' ? cart.description : dialog.dataset.error;
      }
    } catch (_) {
      mutationError = dialog.dataset.error;
    } finally {
      clearTimeout(mutationTimeout);
    }
    if (events.signal.aborted) {
      // The write may have succeeded, but only the current instance may render.
      document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {bubbles: true, detail: {openDrawer: false}}));
      return;
    }
    // Read back after both confirmed and uncertain responses; never repeat a mutation.
    try {
      const rendered = (!mutationError && consume(cart, focus)) || await update(focus);
      if (events.signal.aborted) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {bubbles: true, detail: {openDrawer: false}}));
        return;
      }
      if (mutationError) {
        errorText.textContent = mutationError;
        error.hidden = false;
      }
      document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {
        bubbles: true, detail: {openDrawer: false, cart: mutationError ? undefined : cart, cartDrawerUpdated: Boolean(rendered)}
      }));
    } finally {
      changing = false;
    }
  });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector('[data-park-cart-notification]')) initialize();
  });
})();
